import React from 'react'
import { AlertTriangle, BellRing, Hexagon, Route, ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ControlCenterSummary as Summary } from '../../services/vehicleControlCenterTypes'

export type SummaryTarget = 'zones' | 'rules' | 'alerts'

interface Props {
  summary?: Summary | null
  loading?: boolean
  /** When provided, cards become buttons that jump to the matching tab */
  onSelect?: (target: SummaryTarget) => void
}

const ControlCenterSummary: React.FC<Props> = ({ summary, loading, onSelect }) => {
  const { t } = useTranslation()
  const cards: { label: string, value?: number, icon: React.ElementType, tone: string, target: SummaryTarget }[] = [
    { label: t('vcc_geofences', { defaultValue: 'Geofences' }), value: summary?.geofence_count, icon: Hexagon, tone: 'text-violet-700 bg-violet-50', target: 'zones' },
    { label: t('vcc_corridors', { defaultValue: 'Corridors' }), value: summary?.corridor_count, icon: Route, tone: 'text-blue-700 bg-blue-50', target: 'zones' },
    { label: t('vcc_enabled_rules', { defaultValue: 'Enabled rules' }), value: summary?.enabled_rule_count, icon: ShieldCheck, tone: 'text-emerald-700 bg-emerald-50', target: 'rules' },
    { label: t('vcc_open_alerts', { defaultValue: 'Open alerts' }), value: summary?.unresolved_alert_count, icon: BellRing, tone: 'text-red-700 bg-red-50', target: 'alerts' }
  ]

  return (
    <div className="space-y-2">
      {summary?.configured_but_untracked && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900">
          <AlertTriangle size={17} className="shrink-0" />
          <span>{t('vcc_waiting_tracker', { defaultValue: 'Configured — waiting for tracker. Zones and rules are ready, but no tracker is currently reporting.' })}</span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {cards.map(({ label, value, icon: Icon, tone, target }) => {
          const body = (
            <>
              <span className={`rounded-md p-1.5 ${tone}`}><Icon size={16} /></span>
              <div className="min-w-0 text-left">
                <div className="text-lg font-bold leading-5 text-gray-900">{loading ? '…' : value ?? 0}</div>
                <div className="truncate text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</div>
              </div>
            </>
          )
          if (onSelect) {
            return (
              <button
                key={label}
                type="button"
                onClick={() => onSelect(target)}
                title={t('vcc_open_tab', { defaultValue: 'Open the matching tab' })}
                className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm transition-colors hover:border-blue-300 hover:bg-blue-50/40 focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                {body}
              </button>
            )
          }
          return (
            <div key={label} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm">
              {body}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ControlCenterSummary
